<template>
  <b-modal
    ref="modalInputResi"
    body-class="!p-[24px] text-black w-full !rounded-[12px]"
    content-class="!rounded-[12px]"
    size="md"
    centered
    hide-footer
    hide-header
    @hide="reset"
  >
    <h3 class="text-[18px] font-medium text-center mb-[8px] text-[#0A0A0A]">
      {{ listMode ? 'Daftar Nomor Resi' : 'Input Nomor Resi' }}
    </h3>
    <p class="text-[14px] text-center mb-[24px] text-[#737373]">
      {{ listMode ? '' : 'Kamu bisa input nomor resi langsung atau menggunakan copy-paste dari Excel/Spreadsheet' }}
    </p>
    <div class="flex flex-col gap-[12px] mb-[24px] max-h-[245px] overflow-y-auto custom-scrollbar">
      <div
        v-for="index in (data.total || 1)"
        :key="index"
        class="w-full flex flex-col gap-[4px]"
      >
        <UiInput
          v-model="awbs[index - 1]"
          :prefix="`Paket ${index}`"
          :disabled="listMode"
          placeholder="Input nomor resi"
          :error="!!errors[index - 1]"
          @paste.native="handlePaste($event, index - 1)"
        />
        <span
          v-if="errors[index - 1]"
          class="text-[12px] text-[#EF4444]"
        >
          {{ errors[index - 1] }}
        </span>
      </div>
    </div>
    <Button
      class="w-full rounded-[6px] px-[16px] py-[8px] h-auto flex justify-center items-center gap-[8px]"
      variant="primary"
      :disabled="isLoading"
      @click="listMode ? hide() : handleSave()"
    >
      <b-spinner
        v-if="isLoading"
        small
        class="w-[16px] h-[16px]"
      />
      {{ listMode ? "Tutup" : "Simpan" }}
    </Button>
  </b-modal>
</template>

<script>
/**
 * @component ModalInputResi
 * @description Displays a modal to input airway bills (resi) for a given packet.
 *
 * @example
 * <ModalInputResi ref="modalInputResi" />
 * this.$refs.modalInputResi.show({
 *   data: packet,
 *   onSaveFn: ({ awbs, data }) => { ... }
 * })
 */
import { Input as UiInput, Button } from '@/@core/components/ui'

export default {
  components: {
    UiInput,
    Button,
  },
  data() {
    return {
      data: {},
      awbs: [],
      errors: [],
      isLoading: false,
      checkDuplicateFn: null,
      currentWarehouseIndex: 0,
      currentPacketIndex: -1,
      listMode: false,
    }
  },
  created() {
    this.onSaveFn = null
  },
  methods: {
    show({
      data,
      checkDuplicateFn,
      currentWarehouseIndex,
      currentPacketIndex,
      onSaveFn,
      listMode,
    }) {
      this.data = data || {}
      this.checkDuplicateFn = checkDuplicateFn
      this.currentWarehouseIndex = currentWarehouseIndex || 0
      const targetTotal = Number(this.data.total) || 1
      const existingAwbs = Array.isArray(this.data.awbs) ? this.data.awbs : []
      this.awbs = Array.from({ length: targetTotal }, (_, i) => existingAwbs[i] || '')
      this.errors = new Array(targetTotal).fill('')
      this.isLoading = false
      this.onSaveFn = onSaveFn
      this.listMode = listMode
      this.currentPacketIndex = currentPacketIndex || -1
      this.$refs.modalInputResi.show()
    },
    reset() {
      this.data = {}
      this.awbs = []
      this.errors = []
      this.isLoading = false
      this.onSaveFn = null
      this.listMode = false
      this.currentPacketIndex = -1
    },
    hide() {
      this.$refs.modalInputResi.hide()
    },
    handleCloseModal() {
      this.hide()
    },
    handlePaste(event, startIndex) {
      const text = (event.clipboardData || window.clipboardData)?.getData('Text')
      if (!text) return

      const rows = text.split(/\r?\n/).map(r => r.trim()).filter(Boolean)
      if (rows.length <= 1) return

      event.preventDefault()

      const newAwbs = [...this.awbs]
      const len = Math.min(newAwbs.length - startIndex, rows.length)
      for (let i = 0; i < len; i += 1) {
        newAwbs[startIndex + i] = rows[i]
      }
      this.awbs = newAwbs
    },
    /**
     * Validates input tracking numbers for emptiness and local/remote duplicates.
     * @returns {Promise<{type: string, message: string}>}
     */
    async validate() {
      const targetTotal = Number(this.data.total) || 1
      this.errors = new Array(targetTotal).fill('')
      let hasError = false

      const validAwbs = this.awbs.reduce((acc, awb, index) => {
        const trimmed = awb ? String(awb).trim() : ''
        if (trimmed) {
          acc.push({ awb: trimmed, index })
        }
        return acc
      }, [])

      const isAllFilled = validAwbs.length === targetTotal
      let message = {
        type: isAllFilled ? 'success' : 'warning',
        message: isAllFilled ? 'No. Resi Sudah Diisi' : 'No. Resi Belum Diisi',
      }

      if (validAwbs.length === 0) {
        return {
          type: 'warning',
          message: 'No. Resi Belum Diisi',
        }
      }

      const filledAwbs = []
      const seenLocal = {}

      for (let i = 0; i < validAwbs.length; i += 1) {
        const { awb, index } = validAwbs[i]

        // 1. Check local duplication (within this modal's fields)
        if (seenLocal[awb] !== undefined) {
          this.$set(this.errors, index, `Nomor resi terpakai di Paket ${seenLocal[awb] + 1}`)
          message = { type: 'error', message: 'No. Resi Ganda' }
          hasError = true
        } else {
          // 2. Check global duplication (across all forms/packets)
          const usedBy = this.checkDuplicateFn ? this.checkDuplicateFn(awb) : null
          if (usedBy) {
            let errorMsg = 'Nomor resi terpakai'
            if (usedBy.warehouseIndex !== this.currentWarehouseIndex) {
              errorMsg = `Nomor resi terpakai di Gudang "${usedBy.warehouseName}", Jenis Paket ${usedBy.packetIndex + 1}`
            } else {
              errorMsg = `Nomor resi terpakai di Jenis Paket ${usedBy.packetIndex + 1}`
            }
            this.$set(this.errors, index, errorMsg)
            message = { type: 'error', message: 'No. Resi Ganda' }
            hasError = true
          } else {
            // 3. Register local
            seenLocal[awb] = index
            filledAwbs.push(awb)
          }
        }
      }

      if (filledAwbs.length > 0) {
        this.isLoading = true
        try {
          try {
            const response = await this.$http_new.post('/kompack/api/v1/outbounds/check-awb', {
              awbs: filledAwbs,
            })
            const duplicates = response?.data?.data?.duplicates || []

            for (let i = 0; i < duplicates.length; i += 1) {
              const dup = duplicates[i]
              const dupIndex = seenLocal[dup.awb]
              if (dupIndex !== undefined) {
                this.$set(this.errors, dupIndex, `Nomor resi terpakai di Outbond ID ${dup.outbound_id}`)
                delete seenLocal[dup.awb]
                hasError = true
                message = {
                  type: 'error',
                  message: 'No. Resi Ganda',
                }
              }
            }
          } catch (error) {
            const errorMessage = error?.response?.data?.message || 'Gagal mengecek nomor resi'
            this.$toast_error({
              message: errorMessage,
            })
            for (let i = 0; i < filledAwbs.length; i += 1) {
              const awb = filledAwbs[i]
              const dupIndex = seenLocal[awb]
              if (dupIndex !== undefined) {
                this.$set(this.errors, dupIndex, 'Gagal mengecek nomor resi')
                delete seenLocal[awb]
              }
            }
            hasError = true
            if (message.type !== 'error') {
              message = {
                type: 'error',
                message: 'Gagal Mengecek No. Resi',
              }
            }
          }

          const marketplaceAwbs = Object.keys(seenLocal)
          if (marketplaceAwbs.length > 0) {
            try {
              const mpResponse = await this.$http_new.post('/komship/api/v1/marketplace-orders/check-duplicate', {
                identifiers: marketplaceAwbs,
              })
              const mpDuplicates = mpResponse?.data?.data?.duplicates || []

              for (let i = 0; i < mpDuplicates.length; i += 1) {
                const dup = mpDuplicates[i]
                const dupIndex = seenLocal[dup.identifier]
                if (dupIndex !== undefined) {
                  this.$set(this.errors, dupIndex, `Nomor resi terpakai di Marketplace Order ID ${dup.marketplace_order_id}`)
                  hasError = true
                  message = {
                    type: 'error',
                    message: 'No. Resi Ganda',
                  }
                }
              }
            } catch (error) {
              const errorMessage = error?.response?.data?.message || 'Gagal mengecek nomor resi marketplace'
              this.$toast_error({
                message: errorMessage,
              })
              for (let i = 0; i < marketplaceAwbs.length; i += 1) {
                const awb = marketplaceAwbs[i]
                const dupIndex = seenLocal[awb]
                if (dupIndex !== undefined && !this.errors[dupIndex]) {
                  this.$set(this.errors, dupIndex, 'Gagal mengecek nomor resi')
                }
              }
              hasError = true
              if (message.type !== 'error') {
                message = {
                  type: 'error',
                  message: 'Gagal Mengecek No. Resi',
                }
              }
            }
          }
        } finally {
          this.isLoading = false
        }
      }

      return message
    },
    async handleSave() {
      const message = await this.validate()

      if (['success', 'warning'].includes(message.type)) {
        const { awbs, data } = this
        const targetTotal = Number(data.total) || 1
        const slicedAwbs = Array.from({ length: targetTotal }, (_, i) => awbs[i] || '')
        if (this.onSaveFn) {
          this.onSaveFn({
            data,
            awbs: slicedAwbs,
            message,
          })
        }
        this.hide()
      }
    },
  },
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 8px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 8px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
